import React from "react";
import {
	Alert,
	Button,
	Checkbox,
	ColumnLayout,
	Container,
	FormField,
	Header,
	Input,
	SpaceBetween,
	Spinner,
} from "@cloudscape-design/components";

const getErrorText = (error) => error?.text || error?.message || String(error || "Unknown error");

const copyText = async (text) => {
	if (navigator.clipboard && window.isSecureContext) {
		await navigator.clipboard.writeText(text);
		return;
	}

	const input = document.createElement("textarea");
	input.value = text;
	input.style.position = "fixed";
	input.style.opacity = "0";
	document.body.appendChild(input);
	input.select();
	document.execCommand("copy");
	document.body.removeChild(input);
};

const validateUrl = (value, label) => {
	if (!String(value).includes("{click_id}")) return `${label} must contain {click_id}.`;
	try {
		const url = new URL(value);
		if (!["http:", "https:"].includes(url.protocol)) return `${label} must be an HTTP(S) URL.`;
	} catch (error) {
		return `${label} must be a valid URL.`;
	}
	return "";
};

export default class PlayerProfilePostbacksSection extends React.Component {
	settingsRequestId = 0;
	isActive = false;

	state = {
		loading: true,
		saving: false,
		enabled: false,
		registrationUrl: "",
		ftdUrl: "",
		error: "",
		success: "",
	};

	componentDidMount() {
		this.isActive = true;
		this.load();
	}

	componentDidUpdate(previousProps) {
		if (String(previousProps.uid) !== String(this.props.uid)) this.load();
	}

	componentWillUnmount() {
		this.isActive = false;
		this.settingsRequestId += 1;
	}

	load = () => {
		if (!this.props.uid) return;
		const uid = String(this.props.uid);
		const requestId = ++this.settingsRequestId;
		this.setState({ loading: true, saving: false, error: "", success: "" });
		this.props.Interface.request("get", { class: "data", module: "postbacks", method: "getSettings" }, { id: this.props.uid })
			.then((response) => {
				if (requestId !== this.settingsRequestId || uid !== String(this.props.uid)) return;
				this.setState({
					loading: false,
					enabled: Boolean(response.enabled),
					registrationUrl: response.registrationUrl || "",
					ftdUrl: response.ftdUrl || "",
				});
			})
			.catch((error) => {
				if (requestId === this.settingsRequestId && uid === String(this.props.uid)) this.setState({ loading: false, error: getErrorText(error) });
			});
	};

	save = () => {
		const registrationError = this.state.enabled ? validateUrl(this.state.registrationUrl, "Registration URL") : "";
		const ftdError = this.state.enabled ? validateUrl(this.state.ftdUrl, "FTD URL") : "";
		if (registrationError || ftdError) {
			this.setState({ error: registrationError || ftdError, success: "" });
			return;
		}

		this.setState({ saving: true, error: "", success: "" });
		const uid = String(this.props.uid);
		this.props.Interface.request("set", { class: "data", module: "postbacks", method: "saveSettings" }, {
			uid: this.props.uid,
			enabled: this.state.enabled,
			registrationUrl: this.state.registrationUrl,
			ftdUrl: this.state.ftdUrl,
		})
			.then((response) => {
				if (!this.isActive || uid !== String(this.props.uid)) return;
				this.setState({
					saving: false,
					enabled: Boolean(response.enabled),
					registrationUrl: response.registrationUrl,
					ftdUrl: response.ftdUrl,
					success: "Postback settings saved.",
				});
			})
			.catch((error) => {
				if (this.isActive && uid === String(this.props.uid)) this.setState({ saving: false, error: getErrorText(error) });
			});
	};

	render() {
		if (this.state.loading) return <Spinner />;

		return (
			<Container
				header={<Header variant="h3" actions={<Button variant="primary" loading={this.state.saving} onClick={this.save}>Save</Button>}>Postbacks</Header>}>
				<SpaceBetween size="m">
					{this.state.error && <Alert type="error">{this.state.error}</Alert>}
					{this.state.success && <Alert type="success">{this.state.success}</Alert>}
					<Checkbox checked={this.state.enabled} onChange={({ detail }) => this.setState({ enabled: detail.checked, success: "" })}>
						Postbacks enabled
					</Checkbox>
					{this.state.enabled && (
						<ColumnLayout columns={1}>
							<FormField label="Registration URL" description="Must be an HTTP(S) URL and contain {click_id}. {buyer_id} is optional.">
								<div style={{ display: "flex", gap: 8 }}>
									<div style={{ flex: 1 }}>
										<Input value={this.state.registrationUrl} onChange={({ detail }) => this.setState({ registrationUrl: detail.value, success: "" })} />
									</div>
									<Button iconName="copy" variant="icon" ariaLabel="Copy Registration URL" onClick={() => copyText(this.state.registrationUrl).catch((error) => this.setState({ error: getErrorText(error) }))} />
								</div>
							</FormField>
							<FormField label="FTD URL" description="Must be an HTTP(S) URL and contain {click_id}. {buyer_id} is optional.">
								<div style={{ display: "flex", gap: 8 }}>
									<div style={{ flex: 1 }}>
										<Input value={this.state.ftdUrl} onChange={({ detail }) => this.setState({ ftdUrl: detail.value, success: "" })} />
									</div>
									<Button iconName="copy" variant="icon" ariaLabel="Copy FTD URL" onClick={() => copyText(this.state.ftdUrl).catch((error) => this.setState({ error: getErrorText(error) }))} />
								</div>
							</FormField>
						</ColumnLayout>
					)}
				</SpaceBetween>
			</Container>
		);
	}
}
